import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import { Form, Formik } from 'formik';
import clsx from 'clsx';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button';
import { AppState } from '../../../store';
import { useCommonStyles } from '../../../common/common';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import { onJwtSignIn, onSignInWithOTP } from '../../../store/actions/Auth';
import { loginSchema } from '../../../schemas/loginSchema.schema';
import InputField from '../../../common/InputField';
import { useRegisterStyles } from '..';
import AppLogoComp from '../../../common/AppLogo';

interface LoginOptionState {
	email: string;
	password: string;
	showPassword: boolean;
	rememberMe: boolean;
    phone?: string
}

const Login = () => {
    const history = useHistory();
    const [optionType, setLoginOptionType] = useState('')
    const [values, setValues] = useState<LoginOptionState>({
		email: '',
		password: '',
		showPassword: false,
		rememberMe: false,
        phone: ''
	})

    const { user } = useSelector<AppState, AppState['auth']>(
        ({ auth }) => auth,
    );

    useEffect(() => {
        if (user && user.token) {
            history.push('/products')
        }
    }, [history, user])

    const classes = useRegisterStyles();
    const {
        checkboxRoot, pointer,
        signInButton,
        paper, formRoot
    } = classes
    const { d_f_row_center, mainContainer, w_100 } = useCommonStyles()
    const dispatch = useDispatch()

    const submitHandler = (data: any, setSubmitting: any) => {
		setSubmitting(true);
        if(optionType === 'phone') {
            dispatch(onSignInWithOTP(data.phone));
        } else {
            dispatch(onJwtSignIn({email: data.email, password: data.password})
            );
        }
		setSubmitting(false);
	}
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleClickShowPassword = () => {
		setValues({ ...values, showPassword: !values.showPassword });
	};

	const handleClickRememberMe = () => {
		setValues({ ...values, rememberMe: !values.rememberMe });
	};

    const handleLoginSelectOption = (option: string) => {
        setLoginOptionType(option)
    }

    return (
        <Box className={clsx(mainContainer, d_f_row_center)}>
            {!optionType && 
                <Grid container display="flex" flexDirection="column" alignItems="center">
                    <Grid item sm={2} md={2}></Grid>
                    <Grid item xs={12} sm={8} md={8} style={{ marginBottom: '2rem', backgroundColor: '#d7d5d526', borderRadius: '4px', }}>
                        <AppLogoComp />
                        <Paper className={paper}>
                            <Box
                                mb={{ xs: 3, xl: 4 }}
                                display='flex'
                                flexDirection='column'>
                                <Button
                                    type='button'
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleLoginSelectOption('phone')}
                                    className={clsx(signInButton)}>
                                    Login through OTP
                                </Button>
                            </Box>
                            <Box component="h1" textAlign="center" mb="2vh">OR</Box>
                            <Box
                                mb={{ xs: 3, xl: 4 }}
                                display='flex'
                                flexDirection='column'>
                                <Button
                                    type='button'
                                    color="primary"
                                    variant="contained"
                                    onClick={() => handleLoginSelectOption('email')}
                                    className={clsx(signInButton)}>
                                    Login with user
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item sm={2} md={2}></Grid>
                </Grid>
            }
            {optionType && 
                <Grid container>
                    <Grid item sm={3} lg={4}></Grid>
                    <Grid item xs={12} sm={6} lg={4} style={{ backgroundColor: '#d7d5d526', borderRadius: '4px', }}>
                        <AppLogoComp />
                        <Paper className={paper}>
                            <Formik
                                validateOnChange={true}
                                initialValues={{
                                    ...values
                                }}
                                validationSchema={loginSchema}
                                onSubmit={(data, { setSubmitting }) => submitHandler(data, setSubmitting)}>
                                {({ isSubmitting }) => (
                                    <Form className={formRoot} noValidate autoComplete='off'>
                                        { optionType === 'email' &&
                                            <>
                                                <Box
                                                    mb={{ xs: 2, xl: 4 }}
                                                    component='div'
                                                    fontSize={16}>
                                                    Enter email address
                                                </Box>
                                                <Box>
                                                    <InputField
                                                        placeholder='Enter email address'
                                                        name='email'
                                                        variant='outlined'
                                                        className={clsx(w_100)}
                                                    />
                                                </Box>
                                                <Box
                                                    mt={{ xs: 3, xl: 4 }}
                                                    mb={{ xs: 2, xl: 4 }}
                                                    component='div'
                                                    fontSize={16}>
                                                    Enter password
                                                </Box>
                                                <Box>
                                                    <InputField
                                                        type={values.showPassword ? 'text' : 'password'}
                                                        placeholder='Enter password'
                                                        name='password'
                                                        variant='outlined'
                                                        className={clsx(w_100)}
                                                        InputProps={{
                                                            endAdornment: <InputAdornment position="end">
                                                                <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    onClick={handleClickShowPassword}
                                                                    onMouseDown={handleMouseDownPassword}
                                                                >
                                                                    {values.showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                                </IconButton>
                                                            </InputAdornment>,
                                                        }}
                                                    />
                                                </Box>
                                                <Box
                                                    mb={{ xs: 3, xl: 4 }}
                                                    display='flex'
                                                    flexDirection={{ xs: 'column', sm: 'row' }}
                                                    alignItems={{ sm: 'center' }}
                                                    justifyContent={{ sm: 'space-between' }}
                                                    fontSize={15}>
                                                    <Box
                                                        color='primary.secondary'
                                                        component='span'
                                                        className={pointer}
                                                        fontSize={15}
                                                    >
                                                        <Link to="/forgot-password">Forgot Password?</Link>
                                                    </Box>
                                                    <Box display='flex' alignItems='center'>
                                                        <Checkbox className={clsx(checkboxRoot)} onClick={handleClickRememberMe} />
                                                        <Box component='span'>
                                                            Remember Me
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </>
                                        }
                                        { optionType === 'phone' && <>
                                            <Box
                                                mb={{ xs: 2, xl: 4 }}
                                                component='div'
                                                fontSize={16}>
                                                Enter Mobile
                                            </Box>
                                            <Box mb={2}>
                                                <InputField
                                                    placeholder='Enter phone number'
                                                    name='phone'
                                                    variant='outlined'
                                                    className={clsx(w_100)}
                                                />
                                            </Box></>
                                        }
                                        <Box
                                            mb={{ xs: 3, xl: 4 }}
                                            display='flex'
                                            flexDirection='column'>
                                            <Button
                                                type='submit'
                                                color="primary"
                                                variant="contained"
                                                disabled={isSubmitting}
                                                className={clsx(signInButton)}>
                                                Sign In
                                            </Button>
                                        </Box>
                                        <Box
                                            mb={{ xs: 3, xl: 4 }}
                                            display='flex'
                                            flexDirection='column'>
                                            <Button
                                                color="secondary"
                                                variant="contained"
                                                className={clsx(signInButton)}
                                                onClick={() => setLoginOptionType('')}
                                            >
                                                Choose Other Option
                                            </Button>
                                        </Box>
                                        <Box
                                            display='flex'
                                            flexDirection={'column'}
                                            alignItems={'center'}
                                            justifyContent={ 'center' }
                                            fontSize={15}>
                                            <Box
                                                color='primary.secondary'
                                                component='span'
                                                className={pointer}
                                                fontSize={15}
                                            >
                                                <b>Create a new account </b>, <Link to="/signup">Sign Up</Link> here
                                            </Box>
                                        </Box>
                                    </Form>
                                )}
                            </Formik>
                        </Paper>
                    </Grid>
                    <Grid item sm={3} md={4} lg={4}></Grid>
                </Grid>
            }
        </Box>
    );
};

export default Login;
